import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { clearAnalyticsId, consentFor } from "@/lib/analytics";
import { isLive, metaTrack } from "@/lib/metaPixel";

const KEY = "sq_cookie_consent";
const PREFS = "sq_cookie_prefs";
const VALUES = ["accepted", "rejected", "custom"];
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const GHL_SRC = process.env.REACT_APP_GHL_TRACKING_SRC;
const GHL_ID = process.env.REACT_APP_GHL_TRACKING_ID;

function loadGhl() {
  if (!GHL_SRC || !GHL_ID || document.getElementById("sq-ghl-tracking")) return;
  const s = document.createElement("script");
  s.id = "sq-ghl-tracking"; s.async = true; s.src = GHL_SRC; s.setAttribute("data-tracking-id", GHL_ID);
  document.body.appendChild(s);
}

function loadPixel() {
  if (!PIXEL_ID || window.fbq) return;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq("init", PIXEL_ID);
}

// Meta Pixel: loaded only after cookie consent, one PageView per public route change.
const lastPv = { path: "", t: 0 };
export function MetaPixel() {
  const { pathname } = useLocation();
  const [ok, setOk] = useState(() => consentFor("ads"));
  useEffect(() => {
    const on = () => setOk(consentFor("ads"));
    window.addEventListener("sq-consent-accepted", on);
    return () => window.removeEventListener("sq-consent-accepted", on);
  }, []);
  useEffect(() => {
    if (!ok || isHiddenPath(pathname)) { return; }
    if (isLive()) { loadPixel(); }
    loadGhl();
    const now = Date.now();
    if (lastPv.path === pathname && now - lastPv.t < 2000) { return; }
    lastPv.path = pathname; lastPv.t = now;
    metaTrack("PageView");
  }, [ok, pathname]);
  return null;
}

const g = (b) => (b ? "granted" : "denied");
// Applies analytics and advertising consent separately (Consent Mode v2, PostHog, Meta Pixel).
function apply({ analytics, ads }) {
  try { window.gtag && window.gtag("consent", "update", { analytics_storage: g(analytics), ad_storage: g(ads), ad_user_data: g(ads), ad_personalization: g(ads) }); } catch {}
  try {
    if (analytics) { window.posthog?.opt_in_capturing?.(); } else { clearAnalyticsId(); window.posthog?.opt_out_capturing?.(); }
    if (window.fbq) { window.fbq("consent", ads ? "grant" : "revoke"); }
  } catch {}
}

function Toggle({ id, label, text, checked, onChange, disabled }) {
  return (
    <label htmlFor={id} className="flex items-start justify-between gap-3 py-2.5 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
      <span><b className="text-sm" style={{ color: "#F5F7F6" }}>{label}</b><span className="block text-xs" style={{ color: "#9CA6A1" }}>{text}</span></span>
      <input id={id} data-testid={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} className="mt-1 w-5 h-5 accent-[#3BE477] shrink-0" />
    </label>
  );
}

const HIDDEN_EXACT = ["/story-export", "/snapbid-video-export", "/explainer-export", "/onboarding"];
const HIDDEN_PREFIX = ["/app", "/admin", "/q/", "/c/", "/payment", "/v/"];
const isHiddenPath = (p) => HIDDEN_EXACT.includes(p) || HIDDEN_PREFIX.some((x) => p.startsWith(x));

export function CookieBanner() {
  const [show, setShow] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const v = localStorage.getItem(KEY);
    if (!VALUES.includes(v)) { localStorage.removeItem(KEY); setShow(true); }
    else if (v !== "accepted") apply({ analytics: consentFor("analytics"), ads: consentFor("ads") });
  }, []);

  useEffect(() => {
    const open = () => { setPrefs(null); setShow(true); };
    window.addEventListener("sq-open-cookie", open);
    return () => window.removeEventListener("sq-open-cookie", open);
  }, []);

  const save = (p) => {
    const v = p.analytics && p.ads ? "accepted" : !p.analytics && !p.ads ? "rejected" : "custom";
    localStorage.setItem(KEY, v);
    if (v === "custom") { localStorage.setItem(PREFS, JSON.stringify(p)); } else { localStorage.removeItem(PREFS); }
    apply(p);
    window.dispatchEvent(new Event("sq-consent-accepted"));
    setShow(false);
    setPrefs(null);
  };
  const choose = (v) => save(v === "accepted" ? { analytics: true, ads: true } : { analytics: false, ads: false });
  const openPrefs = () => setPrefs({ analytics: consentFor("analytics"), ads: consentFor("ads") });

  if (!show || isHiddenPath(pathname)) return null;

  const top = pathname.startsWith("/casa/studio") || pathname.startsWith("/casa/progetti") || pathname.startsWith("/impresa/");
  return (
    <div data-testid="cookie-banner" className={`fixed inset-x-0 z-[90] p-3 sm:p-4 sq-demo-in ${top ? "top-0 max-sm:[&_p]:text-[12px]" : "bottom-0"}`}>
      <div className="max-w-3xl mx-auto rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
        style={{ background: "#0D1311", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 20px 60px -20px rgba(0,0,0,0.8)" }}>
        <div className="flex items-start gap-3 flex-1">
          <Cookie size={20} style={{ color: "#3BE477" }} className="mt-0.5 shrink-0" />
          <p className="text-[13px] leading-relaxed" style={{ color: "#9CA6A1" }}>
            Usiamo cookie tecnici necessari e, con il tuo consenso, cookie statistici e pubblicitari (Google Ads, Meta) per migliorare SnapQuote e misurare le campagne.{" "}
            <Link to="/cookie" className="underline" style={{ color: "#F5F7F6" }}>Cookie Policy</Link>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5 shrink-0">
          <button data-testid="cookie-preferences" onClick={openPrefs}
            className="h-10 px-3 rounded-xl text-sm font-medium underline-offset-2 hover:underline" style={{ color: "#9CA6A1" }}>
            Preferenze
          </button>
          <button data-testid="cookie-reject" onClick={() => choose("rejected")}
            className="h-10 px-4 rounded-xl text-sm font-medium transition hover:bg-white/5"
            style={{ border: "1px solid rgba(255,255,255,0.12)", color: "#F5F7F6" }}>
            Rifiuta
          </button>
          <button data-testid="cookie-accept" onClick={() => choose("accepted")}
            className="h-10 px-5 rounded-xl text-sm font-semibold transition active:scale-95"
            style={{ background: "#3BE477", color: "#050706" }}>
            Accetta
          </button>
        </div>
      </div>
      {prefs && (
        <div data-testid="cookie-prefs-panel" className="max-w-3xl mx-auto mt-2 rounded-2xl p-4 sm:p-5" style={{ background: "#0D1311", border: "1px solid rgba(255,255,255,0.12)" }}>
          <p className="font-semibold text-sm mb-1" style={{ color: "#F5F7F6" }}>Preferenze cookie</p>
          <Toggle id="cookie-pref-necessary" label="Tecnici necessari" text="Accesso, sicurezza e funzionamento del sito. Sempre attivi." checked disabled />
          <Toggle id="cookie-pref-analytics" label="Statistici" text="Ci aiutano a capire come viene usato il sito, in forma aggregata." checked={prefs.analytics} onChange={(b) => setPrefs({ ...prefs, analytics: b })} />
          <Toggle id="cookie-pref-ads" label="Pubblicitari" text="Misurano le campagne Google Ads e Meta e le rendono più pertinenti, e attribuiscono le vendite ai partner affiliati Tradedoubler." checked={prefs.ads} onChange={(b) => setPrefs({ ...prefs, ads: b })} />
          <div className="flex justify-end gap-2.5 pt-3">
            <button data-testid="cookie-prefs-cancel" onClick={() => setPrefs(null)} className="h-10 px-4 rounded-xl text-sm" style={{ color: "#9CA6A1" }}>Annulla</button>
            <button data-testid="cookie-prefs-save" onClick={() => save(prefs)} className="h-10 px-5 rounded-xl text-sm font-semibold" style={{ background: "#3BE477", color: "#050706" }}>Salva preferenze</button>
          </div>
        </div>
      )}
    </div>
  );
}
